// Fetcher functions + React Query hooks for the Sector Rotation dashboard.
//
// Auth: we reuse the project's shared authenticated Axios instance
// (`apiClient` from src/api/client.ts) exactly like the treasury-curve +
// index-performance siblings. Its baseURL already includes the `/api/v1`
// version prefix and attaches the WordPress-issued JWT + `forge_session`
// cookie on every request, so BASE_URL below is the BARE domain path only.
//
// The backend mounts this feature at:
//   /api/v1/market-analysis/sector-rotation/*

import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../../../../api/client';
import type {
    SectorAnalyticsDashboard,
    SectorCorrelationMatrix,
    SectorPerformancePoint,
    SectorRRGPoint,
    SectorValuationPoint,
} from '../types';

// NOTE: apiClient.baseURL already contains `/api/v1`, so we must NOT repeat
// it here or every request would 404 on `/api/v1/api/v1/...`.
const BASE_URL = '/market-analysis/sector-rotation';

// Sector Performance Analytics is a SEPARATE backend feature slice, mounted at
// its own `/sector-performance-analytics` prefix (not under sector-rotation).
const PERFORMANCE_BASE_URL = '/market-analysis/sector-performance-analytics';

// Sector analytics are recomputed once per weekday by the ARQ worker
// (22:30 UTC), so we cache heavily to avoid needless refetches.
const ONE_HOUR = 1000 * 60 * 60;

// --- FETCHERS -----------------------------------------------------------
export const fetchSectorDashboard = async (): Promise<SectorAnalyticsDashboard> => {
    const { data } = await apiClient.get<SectorAnalyticsDashboard>(
        `${BASE_URL}/dashboard/sector_analytics`,
    );
    return data;
};

export const fetchSectorRrg = async (): Promise<SectorRRGPoint[]> => {
    const { data } = await apiClient.get<SectorRRGPoint[]>(`${BASE_URL}/sector_rrg`);
    return data;
};

export const fetchSectorRankings = async (): Promise<SectorRRGPoint[]> => {
    const { data } = await apiClient.get<SectorRRGPoint[]>(`${BASE_URL}/sector_rankings`);
    return data;
};

export const fetchSectorCorrelation = async (): Promise<SectorCorrelationMatrix> => {
    const { data } = await apiClient.get<SectorCorrelationMatrix>(
        `${BASE_URL}/sector_correlation_matrix`,
    );
    return data;
};

export const fetchSectorValuation = async (): Promise<SectorValuationPoint[]> => {
    const { data } = await apiClient.get<SectorValuationPoint[]>(`${BASE_URL}/sector_valuation`);
    return data;
};

export const fetchSectorEtfAnalytics = async (
    ticker: string,
): Promise<SectorValuationPoint> => {
    const { data } = await apiClient.get<SectorValuationPoint>(
        `${BASE_URL}/sector_etfanalytics/${ticker}`,
    );
    return data;
};

export const fetchSectorPerformance = async (): Promise<SectorPerformancePoint[]> => {
    const { data } = await apiClient.get<SectorPerformancePoint[]>(
        `${PERFORMANCE_BASE_URL}/latest`,
    );
    return data;
};

// --- HOOKS --------------------------------------------------------------
export const useSectorDashboard = () => {
    return useQuery({
        queryKey: ['sector-rotation', 'dashboard'],
        queryFn: fetchSectorDashboard,
        staleTime: ONE_HOUR,
    });
};

export const useSectorRrg = () => {
    return useQuery({
        queryKey: ['sector-rotation', 'rrg'],
        queryFn: fetchSectorRrg,
        staleTime: ONE_HOUR,
    });
};

export const useSectorRankings = () => {
    return useQuery({
        queryKey: ['sector-rotation', 'rankings'],
        queryFn: fetchSectorRankings,
        staleTime: ONE_HOUR,
    });
};

export const useSectorCorrelation = () => {
    return useQuery({
        queryKey: ['sector-rotation', 'correlation'],
        queryFn: fetchSectorCorrelation,
        staleTime: ONE_HOUR,
    });
};

export const useSectorValuation = () => {
    return useQuery({
        queryKey: ['sector-rotation', 'valuation'],
        queryFn: fetchSectorValuation,
        staleTime: ONE_HOUR,
    });
};

export const useSectorEtfAnalytics = (ticker: string | null, enabled = true) => {
    return useQuery({
        queryKey: ['sector-rotation', 'etf', ticker],
        queryFn: () => fetchSectorEtfAnalytics(ticker as string),
        enabled: enabled && Boolean(ticker),
        staleTime: ONE_HOUR,
    });
};

// Sector Performance snapshot changes only on the nightly worker run, so cache
// heavily (same ONE_HOUR staleTime as its siblings).
export const useSectorPerformance = () => {
    return useQuery({
        queryKey: ['sector-performance', 'latest'],
        queryFn: fetchSectorPerformance,
        staleTime: ONE_HOUR,
    });
};
